import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, Menu, Send } from 'lucide-react';
import { getNextDraw, fmtHMS } from '../lib/shows';
import { useIST } from '../hooks/useIST';
import { ResultsChart } from './ResultsChart';

const HERO_IMG =
  '/hero-raja.jpg';

const lineVariants = {
  hidden: { y: '115%' },
  show: (i) => ({
    y: '0%',
    transition: { duration: 1.2, delay: 0.35 + i * 0.18, ease: [0.16, 1, 0.3, 1] },
  }),
};

export function Hero({ onWatchDraw }) {
  const ref = useRef(null);
  const ist = useIST();
  const next = getNextDraw(ist);
  const [chartOpen, setChartOpen] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <header ref={ref} className="relative flex min-h-screen flex-col overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Golden Raja crowned lion on a treasure throne"
          className="lion-breathe h-[120%] w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-black/30" />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 60% 45% at 62% 40%, rgba(212,175,55,0.18), transparent 70%)' }}
        />
      </motion.div>

      <motion.nav
        className="relative z-10 flex items-center justify-between px-6 py-6 md:px-16"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <span className="font-royal gold-text text-lg font-black uppercase tracking-[0.15em]" data-testid="nav-brand">
          Golden Raja
        </span>
        <div className="flex items-center gap-4">
          <span className="hidden text-[11px] uppercase tracking-[0.3em] text-[#A3A3A3] sm:inline">
            Sikkim · 4 draws daily
          </span>
          <a
            href="https://t.me/RockyAgency8888"
            target="_blank"
            rel="noreferrer"
            data-testid="telegram-channel-btn"
            className="flex items-center gap-2 border border-[#2AABEE]/50 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-[#4FC3F7] transition-colors hover:bg-[#2AABEE] hover:text-white"
          >
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Telegram</span>
          </a>
          <button
            type="button"
            onClick={() => setChartOpen(true)}
            data-testid="home-chart-open"
            className="border border-[#D4AF37]/40 p-2 text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-black"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </motion.nav>
      {chartOpen && <ResultsChart onClose={() => setChartOpen(false)} />}

      <motion.div className="relative z-10 flex flex-1 flex-col justify-center px-6 md:px-16 lg:px-24" style={{ opacity: fade }}>
        <motion.p
          className="mb-8 text-xs uppercase tracking-[0.35em] text-[#D4AF37]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.25 }}
          data-testid="hero-overline"
        >
          The live draw house of the hills
        </motion.p>

        <h1 className="font-royal uppercase leading-[0.95] tracking-wide" style={{ perspective: 900 }}>
          {['Golden', 'Raja'].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-2">
              <motion.span
                className="block text-[15vw] font-black sm:text-[12vw] lg:text-[9.5rem]"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                data-testid={`hero-line-${i}`}
              >
                {line.split('').map((ch, j) => (
                  <span
                    key={j}
                    className="gold-text letter-globe"
                    style={{ animationDelay: `${(i === 0 ? j : j + 6) * 0.45}s` }}
                  >
                    {ch}
                  </span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          className="mt-4 h-px w-40 origin-left bg-[#D4AF37]"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
        />

        <motion.div
          className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="max-w-md text-base leading-relaxed text-[#A3A3A3]">
            Four times a day the wheel turns in Gangtok. One letter, five numbers,
            and a moment the whole hills hold their breath for.
          </p>
          <button
            type="button"
            onClick={onWatchDraw}
            data-testid="hero-cta-button"
            className="group flex w-fit items-center gap-3 rounded-sm border border-[#333333] px-8 py-4 text-sm uppercase tracking-[0.2em] text-[#F5F5F5] transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            Watch the draw
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        className="relative z-10 px-6 pb-8 md:px-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
      >
        <p className="font-display mb-2 text-sm uppercase tracking-[0.25em] text-[#D4AF37]" data-testid="hero-countdown">
          {next.live
            ? `${next.show.label} draw — live now`
            : `Next draw ${next.show.label} in ${fmtHMS(next.secondsLeft)}`}
        </p>
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#666666]">
          11 AM · 12 PM · 5 PM · 7 PM — IST
        </p>
      </motion.div>
    </header>
  );
}
